import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, RotateCcw, Bookmark, StickyNote, History, XCircle, Lock, Languages } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import api, { formatApiError } from "../lib/api";
import { pick, pickList } from "../lib/pick";
import InteractiveSlides from "../components/learn/InteractiveSlides";

const getEmbedUrl = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  return null;
};

const ReviewList = ({ review, t }) => (
  <div className="mt-6 space-y-4" data-testid="quiz-review">
    {review.map((r, i) => (
      <div
        key={i}
        className={`rounded-[20px] p-5 border ${r.correct ? "border-[#1D4ED8]/40 bg-[#1D4ED8]/10" : "border-[#DC2626]/40 bg-[#DC2626]/10"}`}
        data-testid={`quiz-review-item-${i}`}
      >
        <div className="flex items-start gap-3">
          {r.correct ? <CheckCircle2 className="w-5 h-5 text-[#1D4ED8] shrink-0 mt-0.5" /> : <XCircle className="w-5 h-5 text-[#DC2626] shrink-0 mt-0.5" />}
          <p className="font-bold text-sm text-[#0B1B3A]">{r.question}</p>
        </div>
        <div className="mt-3 pl-8 space-y-1 text-sm">
          <p className="text-[#475569]">
            <span className="font-bold uppercase tracking-wider text-[11px]">{t("quiz.yourAnswer")}: </span>
            {r.selected_index != null ? r.options[r.selected_index] : "—"}
          </p>
          {!r.correct && (
            <p className="text-[#1D4ED8] font-semibold">
              <span className="font-bold uppercase tracking-wider text-[11px]">{t("quiz.correctAnswer")}: </span>
              {r.options[r.correct_index]}
            </p>
          )}
        </div>
      </div>
    ))}
  </div>
);

export default function LessonView() {
  const { lessonId } = useParams();
  const { t, lang, setLang } = useLang();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [attempts, setAttempts] = useState([]);
  const [openAttempt, setOpenAttempt] = useState(null);

  const load = () => {
    api
      .get(`/lessons/${lessonId}`)
      .then((r) => {
        setData(r.data);
        setNote(r.data.note || "");
        setBookmarked(r.data.bookmarked);
      })
      .catch((err) => setError(formatApiError(err)));
    api.get(`/lessons/${lessonId}/attempts`).then((r) => setAttempts(r.data)).catch(() => setAttempts([]));
  };

  useEffect(() => {
    setData(null);
    setAnswers({});
    setResult(null);
    setError("");
    setOpenAttempt(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="lesson-error">
        <p className="text-[#DC2626] font-semibold">{error}</p>
        <Link to="/courses" className="btn-outline-light mt-8">{t("course.back")}</Link>
      </div>
    );
  }
  if (!data) return <p className="max-w-7xl mx-auto px-6 py-16 text-[#64748B]" data-testid="lesson-loading">{t("common.loading")}</p>;

  const { lesson, lessons, completed_lessons } = data;
  const currentIndex = lessons.findIndex((l) => l.id === lessonId);
  const nextLesson = lessons[currentIndex + 1];
  const embedUrl = getEmbedUrl(lesson.video_url);
  const isCompleted = completed_lessons.includes(lessonId);
  const allAnswered = lesson.quiz.every((q) => answers[q.id] !== undefined);

  const submitQuiz = async () => {
    if (!allAnswered) return;
    setSubmitting(true);
    try {
      const { data: res } = await api.post(`/lessons/${lessonId}/quiz`, { answers });
      setResult(res);
      if (res.xp_earned) toast.success(`+${res.xp_earned} XP`);
      load();
    } catch (err) {
      setResult(null);
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const retake = () => {
    setAnswers({});
    setResult(null);
    load();
  };

  const saveNote = async () => {
    setSavingNote(true);
    try {
      await api.put(`/lessons/${lessonId}/note`, { content: note });
      toast.success(t("lesson.notesSaved"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingNote(false);
    }
  };

  const toggleBookmark = async () => {
    try {
      const { data: res } = await api.post(`/lessons/${lessonId}/bookmark`);
      setBookmarked(res.bookmarked);
      toast.success(res.bookmarked ? t("lesson.bookmarked") : t("lesson.bookmark"));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-6 py-10 grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="lesson-page">
      {/* Sidebar */}
      <aside className="lg:col-span-4">
        <Link
          data-testid="lesson-back-link"
          to={`/courses/${lesson.course_id}`}
          className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-[#475569] hover:text-[#1D4ED8] transition-colors duration-200"
        >
          <ArrowLeft className="w-4 h-4" /> {t("lesson.backToCourse")}
        </Link>
        <div className="mt-6 card-dark overflow-hidden divide-y divide-[#E2E8F0]">
          {lessons.map((l, i) => {
            const active = l.id === lessonId;
            const done = completed_lessons.includes(l.id);
            const locked = l.unlocked === false;
            const inner = (
              <>
                <span className={`font-heading font-black text-lg w-8 ${active ? "text-[#94A3B8]" : "text-[#94A3B8]"}`}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className={`flex-1 text-sm font-semibold ${active ? "text-[#0B1B3A]" : locked ? "text-[#94A3B8]" : "text-[#0B1B3A]"}`}>
                  {pick(l, "title", lang)}
                  {locked && l.unlock_date && (
                    <span className="block text-[11px] font-normal text-[#B45309] mt-0.5">
                      {t("program.unlocksOn")} {new Date(l.unlock_date).toLocaleDateString("el-GR")}
                    </span>
                  )}
                </span>
                {done ? (
                  <CheckCircle2 className={`w-5 h-5 ${active ? "text-[#0B1B3A]" : "text-[#1D4ED8]"}`} />
                ) : locked ? (
                  <Lock className="w-4 h-4 text-[#94A3B8]" />
                ) : null}
              </>
            );
            return locked ? (
              <div key={l.id} data-testid={`lesson-nav-locked-${l.id}`} className="flex items-center gap-4 p-4 opacity-70">
                {inner}
              </div>
            ) : (
              <Link
                key={l.id}
                to={`/lessons/${l.id}`}
                data-testid={`lesson-nav-${l.id}`}
                className={`flex items-center gap-4 p-4 transition-colors duration-200 ${active ? "bg-[#1D4ED8] text-white" : "hover:bg-[#F8FAFC]"}`}
              >
                {inner}
              </Link>
            );
          })}
        </div>
      </aside>

      {/* Main */}
      <motion.main className="lg:col-span-8" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="flex flex-wrap items-center gap-3">
          <span className="badge-outline">{t("lesson.content")}</span>
          {isCompleted && <span className="badge-lime" data-testid="lesson-completed-badge">{t("lesson.completed")}</span>}
          <button
            data-testid="lesson-bookmark-button"
            onClick={toggleBookmark}
            className={`ml-auto inline-flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-full border transition-colors duration-200 ${
              bookmarked
                ? "bg-[#1D4ED8] text-white border-[#1D4ED8]"
                : "bg-transparent text-[#334155] border-[#E2E8F0] hover:border-[#1D4ED8] hover:text-[#1D4ED8]"
            }`}
          >
            <Bookmark className={`w-4 h-4 ${bookmarked ? "fill-current" : ""}`} />
            {bookmarked ? t("lesson.bookmarked") : t("lesson.bookmark")}
          </button>
        </div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl mt-5 text-[#0B1B3A]" data-testid="lesson-title">{pick(lesson, "title", lang)}</h1>
        {(lesson.content_en || lesson.slides_en) && (
          <div className="inline-flex items-center gap-2 text-xs font-bold mt-4" data-testid="lesson-lang-toggle">
            <Languages className="w-4 h-4 text-[#1D4ED8]" />
            {["el", "en"].map((l) => (
              <button
                key={l}
                data-testid={`lesson-lang-${l}`}
                onClick={() => setLang(l)}
                className={`px-3 py-1.5 rounded-full border transition-colors duration-200 ${lang === l ? "bg-[#1D4ED8] text-white border-[#1D4ED8]" : "border-[#E2E8F0] text-[#334155] hover:border-[#1D4ED8]"}`}
              >
                {l === "el" ? "Ελληνικά" : "English"}
              </button>
            ))}
          </div>
        )}

        {embedUrl && (
          <div className="mt-8 aspect-video rounded-[24px] overflow-hidden border border-[#E2E8F0]">
            <iframe src={embedUrl} title={lesson.title} className="w-full h-full" allowFullScreen data-testid="lesson-video" />
          </div>
        )}
        {!embedUrl && lesson.video_url && lesson.video_url.endsWith(".mp4") && (
          <video controls className="mt-8 w-full rounded-[24px] border border-[#E2E8F0]" src={lesson.video_url} data-testid="lesson-video" />
        )}

        {(lesson.slides || []).length > 0 && (
          <div className="mt-8" data-testid="lesson-interactive-section">
            <InteractiveSlides key={lang} slides={(lang === "en" && lesson.slides_en) || lesson.slides} />
          </div>
        )}

        <details className="card-cream p-7 sm:p-9 mt-8" open={!(lesson.slides || []).length} data-testid="lesson-content-wrap">
          <summary className="cursor-pointer font-heading font-black text-lg text-[#0B1B3A]">{t("live.fullText")}</summary>
          <div className="text-[#0B1B3A]/80 text-[17px] leading-[1.75] whitespace-pre-wrap mt-4" data-testid="lesson-content">
            {pick(lesson, "content", lang)}
          </div>
        </details>

        {/* NOTES */}
        <div className="mt-10 card-dark p-7" data-testid="lesson-notes-section">
          <div className="flex items-center gap-2">
            <StickyNote className="w-5 h-5 text-[#1D4ED8]" />
            <h2 className="font-heading font-black text-xl text-[#0B1B3A]">{t("lesson.notes")}</h2>
          </div>
          <textarea
            data-testid="lesson-notes-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={5}
            placeholder={t("lesson.notesPlaceholder")}
            className="input-flat mt-4 resize-y"
          />
          <button data-testid="lesson-notes-save" onClick={saveNote} disabled={savingNote} className="btn-lime mt-4 !px-6 !py-2.5 text-sm disabled:opacity-50">
            {savingNote ? t("common.loading") : t("lesson.notesSave")}
          </button>
        </div>

        {/* QUIZ */}
        {lesson.quiz.length > 0 && (
          <div className="mt-12 card-dark overflow-hidden" data-testid="quiz-section">
            <div className="bg-[#1D4ED8] px-7 sm:px-9 py-7">
              <h2 className="font-heading font-black text-2xl text-white">{t("quiz.title")}</h2>
              <p className="text-white/80 text-sm mt-1">{t("quiz.subtitle")}</p>
            </div>

            <div className="p-7 sm:p-9 space-y-10">
              {lesson.quiz.map((q, qi) => (
                <div key={q.id} data-testid={`quiz-question-${qi}`}>
                  <p className="font-bold text-lg text-[#0B1B3A]">
                    <span className="text-[#94A3B8] font-heading mr-2">{String(qi + 1).padStart(2, "0")}</span>
                    {pick(q, "question", lang)}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                    {pickList(q, "options", lang).map((opt, oi) => (
                      <button
                        key={oi}
                        type="button"
                        data-testid={`quiz-option-${qi}-${oi}`}
                        onClick={() => !result && setAnswers({ ...answers, [q.id]: oi })}
                        className={`quiz-option text-left px-5 py-3.5 text-sm ${answers[q.id] === oi ? "selected" : ""}`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              {!result ? (
                <div>
                  {!allAnswered && (
                    <p className="text-sm text-[#B45309] font-semibold mb-4" data-testid="quiz-incomplete-warning">{t("quiz.selectAll")}</p>
                  )}
                  <button data-testid="quiz-submit-button" onClick={submitQuiz} disabled={!allAnswered || submitting} className="btn-blue disabled:opacity-40">
                    {submitting ? t("common.loading") : t("quiz.submit")}
                  </button>
                </div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className={`rounded-[24px] p-8 border ${result.passed ? "border-[#1D4ED8]/50 bg-[#1D4ED8]/10" : "border-[#DC2626]/50 bg-[#DC2626]/10"}`}
                  data-testid="quiz-result"
                >
                  <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#64748B]">{t("quiz.score")}</p>
                  <p className={`font-heading font-black text-6xl mt-2 ${result.passed ? "text-[#1D4ED8]" : "text-[#DC2626]"}`} data-testid="quiz-score-value">
                    {result.percentage}%
                  </p>
                  <p className="text-sm mt-2 text-[#64748B]">{result.score} / {result.total}</p>
                  <p className={`font-bold mt-4 ${result.passed ? "text-[#1D4ED8]" : "text-[#DC2626]"}`} data-testid="quiz-result-message">
                    {result.passed ? t("quiz.passed") : t("quiz.failed")}
                  </p>

                  <h3 className="font-heading font-black text-lg mt-8 text-[#0B1B3A]">{t("quiz.review")}</h3>
                  <ReviewList review={result.review || []} t={t} />

                  <div className="flex flex-wrap gap-3 mt-8">
                    <button data-testid="quiz-retake-button" onClick={retake} className="btn-outline-light !px-6 !py-2.5 text-sm">
                      <RotateCcw className="w-4 h-4" /> {t("quiz.retake")}
                    </button>
                    {result.passed && nextLesson && (
                      <Link data-testid="next-lesson-button" to={`/lessons/${nextLesson.id}`} className="btn-lime !px-6 !py-2.5 text-sm">
                        {t("lesson.next")} <ArrowRight className="w-4 h-4" />
                      </Link>
                    )}
                    {result.passed && !nextLesson && (
                      <Link data-testid="back-to-course-button" to={`/courses/${lesson.course_id}`} className="btn-blue !px-6 !py-2.5 text-sm">
                        {t("lesson.backToCourse")}
                      </Link>
                    )}
                  </div>
                </motion.div>
              )}

              {/* ATTEMPT HISTORY */}
              <div className="pt-8 border-t border-[#E2E8F0]" data-testid="quiz-history-section">
                <div className="flex items-center gap-2">
                  <History className="w-5 h-5 text-[#1D4ED8]" />
                  <h3 className="font-heading font-black text-xl text-[#0B1B3A]">{t("quiz.history")}</h3>
                </div>
                {attempts.length === 0 ? (
                  <p className="text-sm text-[#94A3B8] mt-4" data-testid="quiz-history-empty">{t("quiz.historyEmpty")}</p>
                ) : (
                  <div className="mt-4 rounded-[20px] border border-[#E2E8F0] divide-y divide-[#E2E8F0] overflow-hidden">
                    {attempts.map((a, ai) => (
                      <div key={ai} data-testid={`quiz-attempt-${ai}`}>
                        <div className="flex flex-wrap items-center gap-4 p-4">
                          <span className={`font-heading font-black text-2xl ${a.passed ? "text-[#1D4ED8]" : "text-[#DC2626]"}`}>{a.percentage}%</span>
                          <span className="text-sm text-[#64748B]">{a.score} / {a.total}</span>
                          <span className="text-xs text-[#94A3B8]">{new Date(a.created_at).toLocaleString()}</span>
                          <button
                            data-testid={`quiz-attempt-toggle-${ai}`}
                            onClick={() => setOpenAttempt(openAttempt === ai ? null : ai)}
                            className="ml-auto text-xs font-bold uppercase tracking-wider text-[#1D4ED8] hover:underline"
                          >
                            {openAttempt === ai ? t("quiz.hideReview") : t("quiz.showReview")}
                          </button>
                        </div>
                        {openAttempt === ai && (
                          <div className="px-4 pb-4">
                            <ReviewList review={a.review || []} t={t} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </motion.main>
    </div>
  );
}
